import { cn } from '@repo/tailwind-config/tailwind.functions'
import { BuildingTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getCenterTeachingHallCosts } from '../../../../api/centers/center-teaching-hall-costs/get-center-teaching-hall-costs'
import { updateCenterTeachingHallCosts } from '../../../../api/centers/center-teaching-hall-costs/update-center-teaching-hall-costs'
import { getCenter } from '../../../../api/centers/get-center'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import {
  CenterTeachingHallCost,
  CenterTeachingHallCostInput
} from '../../../../lib/types/models/center/center-teaching-hall-costs'
import { DataProps } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import EditableTable, { EditableTableColumn } from '../../../elements/editable-table'
import { useCenterCosts } from './center.hooks'
import {
  centerCostsTableColorMapping,
  CenterCostsTableFooter,
  CenterCostsTableHeader,
  CenterTableSkeleton,
  UploadInvoiceComponent,
  userInvoiceColorMapping
} from './center.ui'
import {
  CenterCostsTableProps,
  centerTeachingHallCostEditableColumns,
  getCenterTeachingHallCostsColumns,
  mapCenterTeachingHallCostToInput
} from './utils'

export function CenterTeachingHallCostsTable(props: DataProps<Center, CenterCostsTableProps>) {
  const { mode, costType, currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()
  const [editable, setEditable] = useState(false)
  const [teachingHallCosts, setTeachingHallCosts] = useState<CenterTeachingHallCostInput[]>()

  const { editableColumns, tooltips } = centerTeachingHallCostEditableColumns[user.role]

  const {
    id: centerId,
    data,
    refetch: refetchCenter
  } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView, lock, unlock, isPending, uploadInvoice, deleteInvoice } =
    useCenterCosts({
      centerId,
      costType,
      table: 'teaching-hall'
    })

  const { data: centerTeachingHallCosts, refetch } = useQuery({
    queryKey: ['center-teaching-hall-costs', centerId],
    queryFn: () => getCenterTeachingHallCosts(centerId)
  })
  useObserver({
    key: 'center-teaching-hall-costs-table',
    update: refetch
  })

  const { mutateAsync } = useMutation({
    mutationFn: (data: CenterTeachingHallCostInput[]) =>
      updateCenterTeachingHallCosts(centerId, data),
    onError: showAxiosError,
    onSuccess: () => {
      refetch()
      centerCostsView.refetch()
      setTeachingHallCosts(undefined)
    }
  })

  const columns: EditableTableColumn<CenterTeachingHallCost>[] = [
    ...getCenterTeachingHallCostsColumns(costType, currency, t, editableColumns, tooltips),
    ...(costType === 'final-cost'
      ? ([
          {
            accessorKey: 'invoices',
            header: t('center.costs.tefl.teachers.and.leaders.table.header.upload.invoices'),
            centerCell: true,
            cell: ({ row }) => (
              <UploadInvoiceComponent
                variant={userInvoiceColorMapping[user.role]}
                invoice={row.original.invoice}
                readOnly={(!editable && mode === 'action-edit') || mode === 'readonly'}
                disabled={isPending}
                onDelete={async () => {
                  await deleteInvoice.mutateAsync(row.original.id)
                  refetch()
                }}
                onChange={async (file) => {
                  await uploadInvoice.mutateAsync({ id: row.original.id, file })
                  refetch()
                }}
              />
            )
          }
        ] as EditableTableColumn<CenterTeachingHallCost>[])
      : [])
  ]

  if (!centerTeachingHallCosts || !data) return <CenterTableSkeleton />
  return (
    <div>
      <CenterCostsTableHeader
        mode={mode}
        icon={BuildingTwoIcon}
        title={t('center.costs.teaching.hall.title')}
        onCancel={() => setTeachingHallCosts(undefined)}
        onEditableChange={setEditable}
        validateOptions={{
          validate: costType === 'final-cost',
          locked: data.isHostFamiliesLocked,
          onLock: lock.mutateAsync,
          onUnlock: unlock.mutateAsync
        }}
        onSave={async () => {
          if (teachingHallCosts) {
            await mutateAsync(teachingHallCosts)
            await Promise.all([refetch(), centerCostsView.refetch(), refetchCenter?.()])
          }
        }}
      />

      <EditableTable<CenterTeachingHallCost>
        isEditable={editable}
        data={centerTeachingHallCosts.data}
        columns={columns}
        footerRowClassName={cn({ 'bg-white': costType === 'final-cost' })}
        onDataChange={(data) => {
          const mappedData = data.map((item) => mapCenterTeachingHallCostToInput(item, costType))
          if (mode === 'free-edit') mutateAsync(mappedData)
          else setTeachingHallCosts(mappedData)
        }}
        footer={
          <CenterCostsTableFooter
            currency={currency}
            costType={costType}
            textColor={centerCostsTableColorMapping[user.role][costType].textColor}
            variant={centerCostsTableColorMapping[user.role][costType].variant}
            label={
              costType === 'final-cost'
                ? t('center.final.costs.fees')
                : t('center.costs.teaching.hall.table.footer.total.teaching.hall')
            }
            total={centerCostsView.data?.totalTeachingHallCost}
            initialAmount={centerCostsView.data?.totalTeachingHallCost}
            finalAmount={centerCostsView.data?.finalTotalTeachingHallCost}
            balance={centerCostsView.data?.teachingHallBalance}
          />
        }
      />
    </div>
  )
}
